import { useId, useState } from 'react'
import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from '@/components/ui'
import type { TriageKindOption, TriageRun } from '@/types/inbox'
import type { BulkVerdict } from './triageBulk'
import {
  APPLY, approveN, BULK_KIND_EXCLUDED, changeKindN, checkingText, CLEAR, dismissN, KEEP_SELECTING, KIND_LABEL, NO_COMMON_KIND,
  rerunN, RUN_BUSY, runEndText, runProgress, SELECT_A_KIND, SELECT_ALL, selectedText,
} from './triageText'

const SELECT_CLASS =
  'text-xs rounded-md border border-border bg-background px-2 py-1 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring'

export interface TriageSelectionBarProps {
  selected: number
  total: number
  allState: boolean | 'indeterminate'
  onToggleAll(): void
  verdict: BulkVerdict
  noun: string
  onApprove(): void
  onRerun(): void
  onDismiss(): void
  /** `'loading'` while the choices are read; null before there is anything to show. */
  changeKind: { choices: TriageKindOption[]; note: string | null } | 'loading' | null
  onOpenChangeKind(): void
  onApplyKind(kindId: string): void
  confirm: { heading: string; lines: string[]; skipped: string[]; action: string; actionDisabled?: boolean } | null
  onConfirm(): void
  onCancelConfirm(): void
  /** How many messages are being checked before the bar can judge them. */
  checking: number | null
  run: TriageRun | null
  onClear(): void
  /** A sentence for a read or run that failed, shown under the bar's controls. */
  error?: string | null
  /** Lines under the bar while a run goes and after it ends: how many messages it left out. */
  notes?: string[]
}

/**
 * SA-1329 (spec §4.1-§4.2): the bar over a selection of triage messages. Props
 * only: the panel and the queue own the selection, the verdict and the run.
 */
export function TriageSelectionBar(p: TriageSelectionBarProps) {
  const reasonId = useId()
  const dismissReasonId = useId()
  const changeReasonId = useId()
  const [kind, setKind] = useState('')
  const running = p.run?.status === 'running'
  const finished = p.run?.status === 'done'

  if (finished && p.run) {
    return (
      <div className="flex flex-wrap items-center gap-2 bg-accent px-3 py-2 text-xs" role="status">
        <span>{runEndText(p.run)}</span>
        {p.notes?.map((line) => <span key={line} className="text-muted-foreground">{line}</span>)}
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={p.onClear}>{CLEAR}</Button>
      </div>
    )
  }

  if (p.confirm) {
    return (
      <div className="space-y-2 bg-accent px-3 py-2 text-xs">
        <div className="font-medium">{p.confirm.heading}</div>
        <ul className="space-y-0.5">
          {p.confirm.lines.map((line) => <li key={line}>{line}</li>)}
        </ul>
        {p.confirm.skipped.length > 0 && (
          <ul className="space-y-0.5 text-muted-foreground">
            {p.confirm.skipped.map((line) => <li key={line}>{line}</li>)}
          </ul>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" className="h-7 text-xs" disabled={p.confirm.actionDisabled} onClick={p.onConfirm}>{p.confirm.action}</Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={p.onCancelConfirm}>{KEEP_SELECTING}</Button>
        </div>
      </div>
    )
  }

  const busy = running || p.checking !== null
  const reason = running ? RUN_BUSY : busy ? null : p.verdict.approve.reason
  const dismissReason = busy ? null : p.verdict.dismiss.enabled ? null : p.verdict.dismiss.reason
  const changeReason = busy ? null : p.verdict.changeKind.enabled ? null : p.verdict.changeKind.reason
  const rerun = p.verdict.approve.mode === 'rerun'
  const approveOn = !busy && p.verdict.approve.enabled
  const dismissOn = !busy && p.verdict.dismiss.enabled
  const changeOn = !busy && p.verdict.changeKind.enabled
  const status = running && p.run ? runProgress(p.run.settled, p.run.items.length)
    : p.checking !== null ? checkingText(p.checking) : null

  return (
    <div className="bg-accent px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <Checkbox checked={p.allState} onCheckedChange={p.onToggleAll} aria-label={SELECT_ALL} />
        <span className="font-medium">{selectedText(p.selected, p.total)}</span>
        {status ? <span className="text-muted-foreground">{status}</span>
          : p.verdict.summary ? <span className="text-muted-foreground">{p.verdict.summary}</span> : null}
        {rerun ? (
          <Button size="sm" className="h-7 text-xs" disabled={!approveOn} aria-describedby={reason ? reasonId : undefined} onClick={p.onRerun}>
            {rerunN(p.selected)}
          </Button>
        ) : (
          <Button size="sm" className="h-7 text-xs" disabled={!approveOn} aria-describedby={reason ? reasonId : undefined} onClick={p.onApprove}>
            {approveN(p.selected)}
          </Button>
        )}
        <Popover onOpenChange={(open) => { if (open) { setKind(''); p.onOpenChangeKind() } }}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs" disabled={!changeOn} aria-describedby={running ? reasonId : changeReason ? changeReasonId : undefined}>{changeKindN(p.selected)}</Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 space-y-2 text-xs">
            {p.changeKind === 'loading' ? <p>{checkingText(p.selected)}</p> : p.changeKind ? (
              <>
                {p.changeKind.note && <p className="text-muted-foreground">{BULK_KIND_EXCLUDED(p.noun)}</p>}
                {p.changeKind.choices.length === 0 ? <p>{NO_COMMON_KIND}</p> : (
                  <div className="flex items-center gap-2">
                    <select aria-label={KIND_LABEL} className={SELECT_CLASS} value={kind} onChange={(e) => setKind(e.target.value)}>
                      <option value="">{SELECT_A_KIND}</option>
                      {p.changeKind.choices.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <Button size="sm" className="h-7 text-xs" disabled={!kind} onClick={() => p.onApplyKind(kind)}>{APPLY}</Button>
                  </div>
                )}
              </>
            ) : null}
          </PopoverContent>
        </Popover>
        <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={!dismissOn} aria-describedby={running ? reasonId : dismissReason ? dismissReasonId : undefined} onClick={p.onDismiss}>
          {dismissN(p.selected)}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={running} aria-describedby={running ? reasonId : undefined} onClick={p.onClear}>{CLEAR}</Button>
      </div>
      {reason && <p id={reasonId} className="mt-1 text-muted-foreground">{reason}</p>}
      {dismissReason && <p id={dismissReasonId} className="mt-1 text-muted-foreground">{dismissReason}</p>}
      {changeReason && <p id={changeReasonId} className="mt-1 text-muted-foreground">{changeReason}</p>}
      {p.notes?.map((line) => <p key={line} className="mt-1 text-muted-foreground">{line}</p>)}
      {p.error && <p role="alert" className="mt-1 text-destructive">{p.error}</p>}
    </div>
  )
}
